import { useState } from 'react';
import { useSlotMachine } from '../../../hooks/useSlotMachine';
import { useWalletStore } from '../../../store/walletStore';
import { ReelAnimation } from './ReelAnimation';

// Presentational slot game. Contains NO odds/payout logic — it only animates the
// result returned by the server (CLAUDE.md §2.5).
export function SlotMachine({ minBet, maxBet }: { minBet: number; maxBet: number }) {
  const { spin, spinning, result, error } = useSlotMachine();
  const balance = useWalletStore((s) => s.balance);
  const [bet, setBet] = useState(minBet || 10);

  const canSpin =
    !spinning && balance !== null && bet >= minBet && bet <= maxBet && bet <= balance;

  const onSpin = async () => {
    try {
      await spin(bet);
    } catch {
      /* error surfaced via hook */
    }
  };

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <div className="card space-y-6 text-center">
        <ReelAnimation reels={result?.reels ?? []} spinning={spinning} />

        {result && !spinning && (
          <p
            className={`text-xl font-bold ${
              result.payout > 0 ? 'text-emerald-400' : 'text-slate-400'
            }`}
          >
            {result.payout > 0 ? `You won ${result.payout} tokens!` : 'No win — spin again!'}
          </p>
        )}

        <div className="flex items-center justify-center gap-3">
          <label className="label mb-0">Bet</label>
          <input
            type="number"
            className="input w-28"
            min={minBet}
            max={maxBet}
            value={bet}
            onChange={(e) => setBet(Math.floor(Number(e.target.value)))}
          />
          <div className="flex gap-1">
            {[minBet, minBet * 5, minBet * 10].map((v) => (
              <button
                key={v}
                className="btn-ghost px-2 py-1 text-xs"
                onClick={() => setBet(Math.min(v, maxBet))}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        <button className="btn-gold w-full text-lg" disabled={!canSpin} onClick={onSpin}>
          {spinning ? 'Spinning…' : `Spin (${bet})`}
        </button>

        {error && <p className="text-sm text-rose-400">{error}</p>}
        <p className="text-xs text-slate-500">
          Bet range {minBet}–{maxBet} tokens
        </p>
      </div>
    </div>
  );
}
